import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import HomeImageStrip from './HomeImageStrip';
import type { HomeVehicleCard } from './homeTypes';

interface ConversionBlockProps {
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  href: string;
  ctaId: string;
  images?: HomeVehicleCard[];
}

const ConversionBlock: React.FC<ConversionBlockProps> = ({
  eyebrow,
  title,
  body,
  ctaLabel,
  href,
  ctaId,
  images = [],
}) => {
  const navigate = useNavigate();
  const hasImages = images.some((v) => Boolean(v.image_url));

  return (
    <section className="py-10 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`grid gap-8 items-center ${
            hasImages ? 'lg:grid-cols-2' : ''
          }`}
        >
          <div className="max-w-xl border-l-2 border-orange-500/40 pl-5 sm:pl-6">
            <p className="text-xs uppercase tracking-[0.14em] text-gray-500 mb-2">{eyebrow}</p>
            <h2 className="heading-section text-xl sm:text-2xl mb-2">{title}</h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-5">{body}</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void apiService.trackEvent('home_cta_clicked', {
                  metadata: { cta: ctaId, surface: 'conversion' },
                });
                navigate(href);
              }}
            >
              {ctaLabel}
            </Button>
          </div>
          {hasImages ? (
            <HomeImageStrip
              images={images.map((v) => ({
                id: v.id,
                image_url: v.image_url,
                alt: v.nickname || v.display_name || `${v.year} ${v.make} ${v.model}`,
              }))}
              max={4}
              size="md"
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export const OrganizerConversion: React.FC<{ images?: HomeVehicleCard[] }> = ({ images }) => (
  <ConversionBlock
    eyebrow="For organizers"
    title="Run events without the spreadsheet"
    body="Registration, check-in, judging, and awards on the same build profiles builders already use."
    ctaLabel="Create event"
    href="/organizer/events/new"
    ctaId="organizer_convert"
    images={images}
  />
);

export const ClubConversion: React.FC<{ images?: HomeVehicleCard[] }> = ({ images }) => (
  <ConversionBlock
    eyebrow="For clubs"
    title="Find your people"
    body="Club profiles, featured builds, and badges that show up where the car already lives."
    ctaLabel="Browse clubs"
    href="/clubs"
    ctaId="club_convert"
    images={images}
  />
);

export default ConversionBlock;
